import { Flex, Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { Chip, FormSummaryItem, Skeleton, Text, Well } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { CampaignContext } from '../CampaignEditPage/CampaignEditPage.context';

import * as styles from './CampaignSummary.css';

interface IProps {
  campaign: Campaign;
  validationErrors?: {
    filters?: string[];
  };
}

function filterGeoTargetings(trgt: Targeting): boolean {
  /*
   * Many types of targetings can belong to a campaign, but we only want geofilter targetings
   * that point to a group (target data).
   */
  return trgt.get('kind') === 'geo' && !trgt.get('is_archived') && !!trgt.get('target_data');
}

function filterAgeDemographicTargetings(trgt: Targeting): boolean {
  return (
    trgt.get('kind') === 'age_demographic' && !trgt.get('is_archived') && !!trgt.get('target_data')
  );
}

function filterInterestTargetings(trgt: Targeting): boolean {
  return trgt.get('kind') === 'interest' && !trgt.get('is_archived') && !!trgt.get('target_data');
}

const FiltersSummary = observer(({ validationErrors, campaign }: IProps): JSX.Element => {
  const { t } = useTranslation();
  const { GeoFilters, Targetables } = useStore();
  const { targetings } = useContext(CampaignContext);
  const theme = useMantineTheme();
  const { isOmniCampaign } = campaign;
  const none = t('(None)');

  const geoFilteredTargetings = targetings?.models.filter(filterGeoTargetings) ?? [];
  const ageDemographicFilteredTargetings =
    targetings?.models.filter(filterAgeDemographicTargetings) ?? [];
  const interestFilteredTargetings = targetings?.models.filter(filterInterestTargetings) ?? [];

  const hasTargetings =
    geoFilteredTargetings.length > 0 ||
    ageDemographicFilteredTargetings.length > 0 ||
    interestFilteredTargetings.length > 0;

  const geoTargetingListItems = geoFilteredTargetings.map((gf) => {
    const trgt = Targetables.get(gf.get('target_data')!);
    if (trgt.get('geo_filter')) {
      const geofilter = GeoFilters.get(trgt.get('geo_filter')!);
      return (
        <li key={gf.id}>
          {(function (): string {
            const address = geofilter.get('address');
            const kind = geofilter.get('kind');
            const name = trgt.get('name');

            if (['Region', 'City'].includes(kind)) {
              return t('{{name}} | {{kind}} | Country: {{country}} | Region: {{region}}', {
                country: address.country,
                kind,
                name,
                region: address.region,
              });
            } else if (['City'].includes(kind)) {
              return t('{{name}} | {{kind}} | Country: {{country}}', {
                country: address.country,
                kind,
                name,
              });
            }
            return t('{{name}} | {{kind}}', { kind, name });
          })()}
        </li>
      );
    } else {
      return null;
    }
  });

  const ageDemographicTargetingListItems = ageDemographicFilteredTargetings.map((ad) => {
    return (
      <Chip className={styles.chipFix} key={ad.id}>
        {ad.get('name')} years
      </Chip>
    );
  });

  const interestTargetingListItems = interestFilteredTargetings.map((interest) => {
    return (
      <Chip className={styles.chipFix} key={interest.id}>
        {interest.get('name')}
      </Chip>
    );
  });

  const omniCampaignFilters = (
    <Well layout={'vertical'}>
      <Stack>
        <div data-name={'filters_locations'}>
          <Text.DefaultMedium className={styles.bottomMargin}>{t('Locations')}</Text.DefaultMedium>
          {geoTargetingListItems.length > 0 ? <ol>{geoTargetingListItems}</ol> : <p>{none}</p>}
        </div>
        <div data-name={'filters_age_demographics'}>
          <Text.DefaultMedium>{t('Age Demographics')}</Text.DefaultMedium>
          <Flex gap={theme.spacing['2']} mt={'2'} wrap={'wrap'}>
            {ageDemographicTargetingListItems.length > 0 ? (
              ageDemographicTargetingListItems
            ) : (
              <p>{none}</p>
            )}
          </Flex>
        </div>
        <div data-name={'filters_interests'}>
          <Text.DefaultMedium>{t('Interests')}</Text.DefaultMedium>
          <Flex gap={theme.spacing['2']} mt={'2'} wrap={'wrap'}>
            {interestTargetingListItems.length > 0 ? interestTargetingListItems : <p>{none}</p>}
          </Flex>
        </div>
      </Stack>
    </Well>
  );

  const locationFilters = <ol>{geoTargetingListItems}</ol>;

  const formSummary = isOmniCampaign ? omniCampaignFilters : locationFilters;

  return (
    <FormSummaryItem
      errors={validationErrors?.filters}
      label={t('Filters')}
      name={'summary_filters'}
      value={
        targetings?.isPending ? (
          <ol>
            <li>
              <Skeleton width={100} />
            </li>
          </ol>
        ) : hasTargetings ? (
          formSummary
        ) : (
          none
        )
      }
    />
  );
});

FiltersSummary.displayName = 'FiltersSummary';

export default FiltersSummary;
